import * as echarts from 'echarts';
import { memo, useEffect, useRef } from 'react';
import { normalizePercentages } from './percentage';
import styles from './style.module.scss';

interface IProps {
    metrics: readonly {
        color: string;
        label: string;
    }[];
    values: number[];
}

export const REMAINING_COLOR = '#e9ebed';

const formatPercentage = (value: string | number) => {
    const percentage = Number(value);

    return `${Number.isInteger(percentage) ? percentage : percentage.toFixed(2)}%`;
};

function FleetUtilizationChart({ metrics, values }: IProps) {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<echarts.ECharts>();

    useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;

        const chart = echarts.init(container);
        chartRef.current = chart;
        const resizeObserver = new ResizeObserver(() => chart.resize());
        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
            chart.dispose();
            chartRef.current = undefined;
        };
    }, []);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;

        let remainingPercentage = 100;
        const normalizedValues = normalizePercentages(values);
        const data: echarts.PieSeriesOption['data'] = metrics.map((metric, index) => {
            const value = Number.isFinite(normalizedValues[index])
                ? Math.min(normalizedValues[index], remainingPercentage)
                : 0;
            remainingPercentage -= value;

            return {
                name: metric.label,
                value,
                itemStyle: { color: metric.color },
            };
        });

        if (remainingPercentage > 0.000001) {
            data.push({
                name: 'No data',
                value: remainingPercentage,
                itemStyle: { color: REMAINING_COLOR },
                emphasis: {
                    itemStyle: { color: REMAINING_COLOR },
                },
            });
        }

        chart.setOption({
            animation: true,
            animationDuration: 0,
            stateAnimation: {
                duration: 200,
                easing: 'cubicOut',
            },
            grid: {
                top: '15px',
                left: '15px',
                right: '15px',
                bottom: '15px',
            },
            aria: {
                enabled: true,
                description: 'Fleet utilization chart',
            },
            tooltip: {
                trigger: 'item',
                appendTo: 'body',
                position: 'right',
                valueFormatter: formatPercentage,
            },
            series: [{
                type: 'pie',
                radius: '88%',
                center: ['50%', '50%'],
                clockwise: true,
                startAngle: 90,
                stillShowZeroSum: false,
                label: { show: false },
                labelLine: { show: false },
                emphasis: {
                    scale: true,
                    scaleSize: 8,
                },
                data,
                itemStyle: {
                    borderColor: '#fff',
                    borderWidth: 1,
                },
            }],
        });

    }, [metrics, values]);

    return <div ref={chartContainerRef} className={styles.pie} />;
}

export default memo(FleetUtilizationChart);
